import React, { useState } from 'react';
import supabase from '../../supabase/supabase';

/**
 * Bootstrap Superadmin Page
 * 
 * One-time use page to create the first platform_owner.
 * Only works when no superadmins exist in the database.
 */
const BootstrapSuperadmin = () => {
  const [email, setEmail] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleBootstrap = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('api', {
        body: {
          path: 'platform/bootstrap',
          user_email: email,
          secret_key: secretKey
        }
      });

      if (invokeError) {
        setError(invokeError.message);
      } else if (data?.error) {
        setError(data.error);
      } else {
        setResult(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Bootstrap Superadmin</h1>
          <p className="text-gray-500 mt-2">Create the first platform owner</p>
        </div>

        {result ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2">✓ Success!</h3>
            <p className="text-green-700 text-sm mb-3">
              Superadmin created for: <strong>{result.admin?.user_id || email}</strong>
            </p>
            <p className="text-green-600 text-sm">
              Now sign out and sign back in to get your new permissions, then navigate to:
            </p>
            <code className="block bg-green-100 p-2 rounded mt-2 text-sm">
              /#/platform-admin
            </code>
          </div>
        ) : (
          <form onSubmit={handleBootstrap} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Must be an existing user in the system
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bootstrap Secret Key
              </label>
              <input
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="Enter the PLATFORM_BOOTSTRAP_SECRET"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Set in Supabase Edge Function Secrets
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !email || !secretKey}
              className="w-full py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Creating...' : 'Create Platform Owner'}
            </button>

            <div className="text-center">
              <a href="/#/login" className="text-sm text-purple-600 hover:underline">
                ← Back to Login
              </a>
            </div>
          </form>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-400 text-center">
            This page only works once — when no superadmins exist.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BootstrapSuperadmin;
